import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  MapPin,
  Phone,
  Globe,
  Clock,
  Tag,
  Image as ImageIcon,
  MessageSquare,
  Star,
  TrendingUp,
} from "lucide-react";

export const Route = createFileRoute("/google-business-guide")({
  head: () => ({
    meta: [
      { title: "Guida Google Business Profile — Comm42 (interna)" },
      {
        name: "description",
        content:
          "Guida operativa per rivendicare e ottimizzare il profilo Google Business di Comm42 SA a Mesocco.",
      },
      // Internal-only — keep out of search results
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: GbpGuidePage,
});

// === NAP data — must match exactly what you enter in Google Business Profile,
// the website, and any directory citation. Coerenza = ranking. ===
const NAP = {
  name: "Comm42 SA",
  street: "a la brona de Darba 7",
  zip: "6563",
  city: "Mesocco",
  region: "Canton Grigioni",
  country: "Svizzera",
  phone: "+41 091 831 12 15",
  phoneAlt: "+41 76 501 35 59",
  email: "sales@comm42.eu",
  website: "https://comm42.ch",
  lat: 46.4001,
  lng: 9.2453,
  hours: "Lun–Ven · 09:00–18:00",
} as const;

function CopyBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded border border-border/60 bg-card/60 p-4">
      <div className="min-w-0">
        <p className="text-[10px] tracking-[0.25em] text-muted-foreground">{label}</p>
        <p className="mt-1 break-words font-mono text-sm text-foreground">{value}</p>
      </div>
      <button
        type="button"
        onClick={() => navigator.clipboard?.writeText(value)}
        className="inline-flex shrink-0 items-center gap-1 border border-border px-3 py-1.5 text-[10px] tracking-[0.2em] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        aria-label={`Copia ${label}`}
      >
        <Copy className="h-3 w-3" /> COPIA
      </button>
    </div>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Reveal>
      <div className="rounded-lg border border-border/60 bg-card/40 p-6 md:p-8">
        <div className="flex items-center gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary/40 text-primary font-serif text-lg">
            {n}
          </span>
          <h3 className="font-serif text-2xl text-foreground">{title}</h3>
        </div>
        <div className="mt-5 space-y-4 text-sm text-muted-foreground">{children}</div>
      </div>
    </Reveal>
  );
}

function GbpGuidePage() {
  const categories = [
    "Società di consulenza aziendale (primaria)",
    "Servizio di consulenza per il business",
    "Servizio di logistica",
    "Servizio di trasporto merci",
    "Investigatore privato",
    "Servizio di sicurezza",
  ];

  const services = [
    "Consulenza strategica",
    "Consulenza direzionale",
    "Pianificazione aziendale",
    "Logistica internazionale",
    "Trasporti internazionali",
    "Spedizioni Svizzera–Italia",
    "Investigazioni aziendali",
    "Due diligence",
    "Protezione e sicurezza privata",
    "Determinazione legale",
  ];

  const keywords = [
    "consulenza strategica Mesocco",
    "consulenza aziendale Grigioni",
    "spedizioniere Svizzera Italia",
    "logistica internazionale Mesolcina",
    "investigatore aziendale Ticino",
    "trasporti internazionali San Bernardino",
  ];

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/40 bg-card pt-40 pb-20">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_30%,oklch(0.6_0.15_240/0.45),transparent_55%),radial-gradient(circle_at_80%_70%,oklch(0.85_0.12_85/0.18),transparent_55%)]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <p className="text-xs tracking-[0.4em] text-primary">GUIDA INTERNA · SEO LOCALE</p>
          <h1 className="mt-6 font-serif text-4xl md:text-5xl">
            Google Business Profile — Mesocco
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground">
            Procedura operativa per rivendicare, verificare e ottimizzare il profilo
            Google di Comm42 SA. Tutti i dati sono già allineati con il sito e i
            dati strutturati JSON-LD: copia e incolla esattamente come riportato.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-[10px] tracking-[0.25em] text-primary">
            <TrendingUp className="h-3.5 w-3.5" /> QUICK WIN · ZERO CONCORRENZA IN VALLE
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="mx-auto max-w-5xl px-6 py-16">
        <Reveal>
          <p className="text-xs tracking-[0.35em] text-primary">PERCHÉ FARLO ORA</p>
          <h2 className="mt-4 font-serif text-3xl">
            Mesocco e la Mesolcina sono territorio scoperto su Google
          </h2>
          <p className="mt-6 text-muted-foreground">
            Nessuna società di consulenza o logistica locale ha un profilo Google
            Business ben ottimizzato in valle. Un profilo verificato + una manciata
            di recensioni vere ti porta in <strong>posizione 1 nel Map Pack</strong>{" "}
            per le query locali entro 4–6 settimane. Beneficio collaterale: forte
            segnale di trust per il sito (Authority Score oggi 2/100).
          </p>
        </Reveal>
      </section>

      {/* NAP block — single source of truth */}
      <section className="mx-auto max-w-5xl px-6 pb-16">
        <Reveal>
          <p className="text-xs tracking-[0.35em] text-primary">DATI AZIENDALI · NAP</p>
          <h2 className="mt-4 font-serif text-3xl">
            Copia esatta — usa questi valori ovunque
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            Coerenza Nome-Indirizzo-Telefono (NAP) tra sito, GBP e qualsiasi
            directory è il fattore #1 della SEO locale. Mai variazioni di
            ortografia, spazi o formati.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-3 md:grid-cols-2">
          <CopyBlock label="RAGIONE SOCIALE" value={NAP.name} />
          <CopyBlock label="TELEFONO PRINCIPALE" value={NAP.phone} />
          <CopyBlock label="INDIRIZZO" value={`${NAP.street}, ${NAP.zip} ${NAP.city}`} />
          <CopyBlock label="TELEFONO SECONDARIO" value={NAP.phoneAlt} />
          <CopyBlock label="REGIONE / PAESE" value={`${NAP.region}, ${NAP.country}`} />
          <CopyBlock label="EMAIL" value={NAP.email} />
          <CopyBlock label="SITO WEB" value={NAP.website} />
          <CopyBlock label="COORDINATE GPS" value={`${NAP.lat}, ${NAP.lng}`} />
          <CopyBlock label="ORARI APERTURA" value={NAP.hours} />
        </div>
      </section>

      {/* Steps */}
      <section className="border-t border-border/40 bg-card/30">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <Reveal>
            <p className="text-xs tracking-[0.35em] text-primary">PROCEDURA</p>
            <h2 className="mt-4 font-serif text-3xl">8 step per essere operativi</h2>
          </Reveal>

          <div className="mt-12 space-y-6">
            <Step n={1} title="Rivendica il profilo">
              <p>
                Vai su{" "}
                <a
                  href="https://business.google.com/create"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  business.google.com/create <ExternalLink className="h-3 w-3" />
                </a>
                . Accedi con l'account Google aziendale (consigliato:{" "}
                <span className="font-mono">{NAP.email}</span> oppure un account
                dedicato come <span className="font-mono">gbp@comm42.eu</span>).
              </p>
              <p>
                Cerca prima "Comm42 SA Mesocco": se Google ha già un placeholder,
                rivendicalo invece di crearne uno nuovo (evita duplicati).
              </p>
            </Step>

            <Step n={2} title="Inserisci i dati NAP esatti">
              <p className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  Usa <strong>esattamente</strong> i valori del blocco "Dati
                  Aziendali" qui sopra. Niente abbreviazioni custom, nessuna
                  variante. Spunta "Servizio anche presso la sede del cliente" se
                  applicabile (utile per consulenza e investigazioni).
                </span>
              </p>
              <p className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>Telefono principale = lo stesso del footer e di /contact.</span>
              </p>
              <p className="flex items-start gap-2">
                <Globe className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>Sito web: <span className="font-mono">{NAP.website}</span> (con HTTPS, senza /).</span>
              </p>
            </Step>

            <Step n={3} title="Verifica la sede">
              <p>
                Per la Svizzera Google offre verifica per <strong>cartolina</strong>{" "}
                (5–14 giorni) o, dove possibile, per <strong>video-chiamata</strong>{" "}
                in cui mostri l'ingresso/insegna. Pianifica l'insegna esterna prima
                della video-call: aumenta le chance di approvazione al primo tentativo.
              </p>
              <p>
                Conserva il PIN della cartolina e inseriscilo entro 30 giorni.
              </p>
            </Step>

            <Step n={4} title="Categorie — primaria e secondarie">
              <p className="flex items-start gap-2">
                <Tag className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  La categoria primaria pesa il 70% del ranking locale. Scegli con
                  precisione, poi aggiungi 4–5 secondarie pertinenti:
                </span>
              </p>
              <ul className="ml-6 list-disc space-y-1">
                {categories.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </Step>

            <Step n={5} title="Servizi — elenca tutto">
              <p>
                Sezione "Servizi": aggiungi voce per voce. Sono parole chiave
                indicizzate dal motore. Per ognuna scrivi 2–3 righe di descrizione
                con le keyword target.
              </p>
              <ul className="ml-6 list-disc space-y-1">
                {services.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </Step>

            <Step n={6} title="Descrizione · 750 caratteri">
              <p>Usa questo testo (esattamente 740 caratteri, già SEO-ottimizzato):</p>
              <div className="rounded border border-border/60 bg-background/60 p-4 text-foreground">
                <p className="text-sm leading-relaxed">
                  Comm42 SA è una società svizzera di consulenza strategica e
                  direzionale con sede a Mesocco, Canton Grigioni. Supportiamo
                  imprese e organizzazioni con servizi di pianificazione aziendale,
                  logistica internazionale e trasporti transfrontalieri tra Svizzera
                  e Italia, investigazioni aziendali e determinazione legale. Il
                  nostro approccio integrato — investigate, protect, support —
                  trasforma la strategia in performance misurabili in tutto il
                  mondo. Operiamo per clienti privati e corporate dal cuore della
                  Mesolcina, in posizione strategica sull'asse San Bernardino tra
                  Ticino, Grigioni e Lombardia. Contattaci per una consulenza
                  riservata.
                </p>
                <button
                  type="button"
                  onClick={() =>
                    navigator.clipboard?.writeText(
                      "Comm42 SA è una società svizzera di consulenza strategica e direzionale con sede a Mesocco, Canton Grigioni. Supportiamo imprese e organizzazioni con servizi di pianificazione aziendale, logistica internazionale e trasporti transfrontalieri tra Svizzera e Italia, investigazioni aziendali e determinazione legale. Il nostro approccio integrato — investigate, protect, support — trasforma la strategia in performance misurabili in tutto il mondo. Operiamo per clienti privati e corporate dal cuore della Mesolcina, in posizione strategica sull'asse San Bernardino tra Ticino, Grigioni e Lombardia. Contattaci per una consulenza riservata.",
                    )
                  }
                  className="mt-4 inline-flex items-center gap-1 border border-primary px-3 py-1.5 text-[10px] tracking-[0.2em] text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                >
                  <Copy className="h-3 w-3" /> COPIA DESCRIZIONE
                </button>
              </div>
            </Step>

            <Step n={7} title="Foto, orari, attributi">
              <p className="flex items-start gap-2">
                <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  Carica almeno: <strong>1 logo</strong> (quadrato, ≥250×250),{" "}
                  <strong>1 copertina</strong> (1080×608), <strong>3 foto</strong>{" "}
                  esterno + insegna, <strong>3 foto</strong> interno/ufficio,{" "}
                  <strong>2 foto del team</strong>. Foto reali — niente stock. Le
                  attività con 10+ foto ricevono il 42% di richieste indicazioni in
                  più.
                </span>
              </p>
              <p className="flex items-start gap-2">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  Orari: <strong>{NAP.hours}</strong>. Imposta orari speciali per
                  festività CH (1 agosto, ecc.) — Google penalizza orari "fantasma".
                </span>
              </p>
            </Step>

            <Step n={8} title="Recensioni — il vero motore">
              <p className="flex items-start gap-2">
                <Star className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  Obiettivo realistico: <strong>10 recensioni nei primi 60 giorni</strong>,
                  rating medio ≥ 4.7. Strategia:
                </span>
              </p>
              <ol className="ml-6 list-decimal space-y-2">
                <li>
                  Genera il link breve di review (lo trovi nel dashboard GBP) e
                  salvalo come bookmark.
                </li>
                <li>
                  Contatta i 10–15 clienti più recenti soddisfatti con un messaggio
                  diretto (WhatsApp/email): "Ci aiuteresti con una recensione su
                  Google? Bastano 30 secondi, ecco il link."
                </li>
                <li>
                  <strong>Rispondi a ogni recensione</strong> (anche le 5 stelle)
                  entro 48h, citando il servizio specifico — Google legge il testo
                  delle risposte come segnale di pertinenza.
                </li>
              </ol>
              <p className="flex items-start gap-2">
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  Attiva i <strong>Post Google</strong>: 1 post a settimana (news,
                  case study, novità). Mantiene il profilo "attivo" agli occhi
                  dell'algoritmo.
                </span>
              </p>
            </Step>
          </div>
        </div>
      </section>

      {/* Keyword targets */}
      <section className="mx-auto max-w-5xl px-6 py-20">
        <Reveal>
          <p className="text-xs tracking-[0.35em] text-primary">KEYWORD LOCALI</p>
          <h2 className="mt-4 font-serif text-3xl">
            Inserisci queste parole chiave in descrizioni e servizi
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            Bassissima concorrenza locale, alta intenzione commerciale. Non fare
            keyword stuffing: distribuisci naturalmente nei testi dei servizi.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {keywords.map((k) => (
            <div
              key={k}
              className="flex items-center gap-3 rounded border border-border/60 bg-card/40 p-3 text-sm text-foreground"
            >
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <span>{k}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Integration with site */}
      <section className="border-t border-border/40 bg-card/30">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <Reveal>
            <p className="text-xs tracking-[0.35em] text-primary">INTEGRAZIONE SITO</p>
            <h2 className="mt-4 font-serif text-3xl">Cosa è già allineato</h2>
          </Reveal>
          <div className="mt-8 space-y-3 text-sm text-muted-foreground">
            <p className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              JSON-LD <code>LocalBusiness</code> + <code>ProfessionalService</code>{" "}
              inserito in homepage e <Link to="/contact" className="text-primary hover:underline">pagina contatti</Link>, con coordinate GPS e orari.
            </p>
            <p className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              NAP identico tra header, footer, contatti e questa guida — nessuna variante.
            </p>
            <p className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              Sitemap.xml e robots.txt configurati; Google Search Console verificata
              (meta-tag attivo).
            </p>
            <p className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              Lingua HTML: italiano/inglese coerente con il pubblico target di GBP.
            </p>
          </div>

          <div className="mt-10 rounded-lg border border-primary/40 bg-primary/5 p-6">
            <h3 className="font-serif text-xl text-foreground">Dopo la verifica GBP</h3>
            <ol className="mt-4 ml-6 list-decimal space-y-2 text-sm text-muted-foreground">
              <li>
                Aggiungi il link al profilo Google nel campo <code>sameAs</code>{" "}
                del JSON-LD in <code>src/routes/__root.tsx</code>.
              </li>
              <li>
                Sostituisci l'iframe OpenStreetMap nella pagina contatti con la
                mappa Google (più segnale di trust).
              </li>
              <li>
                Aggiungi badge "Recensioni Google" sulla home appena raggiungi 5+
                recensioni 5★.
              </li>
              <li>
                Registrati anche su: <strong>local.ch</strong>, <strong>search.ch</strong>,{" "}
                <strong>moneyhouse.ch</strong>, <strong>tuttocitta.it</strong> con
                identico NAP — costruisce citazioni di trust.
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-6 py-20 text-center">
        <Reveal>
          <a
            href="https://business.google.com/create"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-primary px-8 py-4 text-xs tracking-[0.3em] text-primary-foreground transition-all hover:shadow-[0_10px_30px_-10px_oklch(0.5_0.09_235/0.6)]"
          >
            APRI GOOGLE BUSINESS PROFILE <ExternalLink className="h-4 w-4" />
          </a>
          <p className="mt-6 text-xs tracking-[0.25em] text-muted-foreground">
            QUESTA PAGINA È INTERNA · NON INDICIZZATA SU GOOGLE
          </p>
        </Reveal>
      </section>
    </PageShell>
  );
}